import type { DurationBreakdown, DurationIssue } from "@/lib/api";
import { localization, type Translate } from "@/app/localization";

/** User-facing wording for a session-duration split that is not exact. The internal
 *  breakdown / issue names never reach the screen. */
export interface DurationQualityNote {
  label: string;
  detail: string;
}

/** How a `partial` split is off: `incomplete` can only be too low, `review` either way. */
export type DurationQuality = "incomplete" | "review";

/** Why a duration shows "—": nothing beyond charging was recorded, nothing was, or the split is partial. */
export type DurationUnknownCode = "charging_only" | "unknown" | DurationQuality;

// Issues that can only make the sums too LOW (an interval missing or unclassified).
// Anything else — including an issue this build does not know — needs review.
const UNDER_COUNT_ISSUES: ReadonlySet<string> = new Set<DurationIssue>([
  "possibly_truncated",
  "unknown_segment_type",
]);

/** The quality of a `partial` split; null when the split is exact, unknown, or absent. */
export function durationQuality(
  breakdown: DurationBreakdown | null | undefined,
  issues: readonly string[] | null | undefined,
): DurationQuality | null {
  if (breakdown !== "partial") return null;
  const list = issues ?? [];
  return list.length > 0 && list.every((i) => UNDER_COUNT_ISSUES.has(i)) ? "incomplete" : "review";
}

/** The code explaining a value the backend left unknown; null when there is nothing to explain. */
export function durationUnknownCode(
  breakdown: DurationBreakdown | null | undefined,
  issues: readonly string[] | null | undefined,
): DurationUnknownCode | null {
  switch (breakdown) {
    case "charging_only":
    case "unknown":
      return breakdown;
    case "partial":
      return durationQuality(breakdown, issues);
    default:
      return null;
  }
}

/** The note for a `partial` split, in the active catalog; null when the split is exact, unknown, or absent. */
export function durationQualityNote(
  breakdown: DurationBreakdown | null | undefined,
  issues: readonly string[] | null | undefined,
  t: Translate = localization.t,
): DurationQualityNote | null {
  const quality = durationQuality(breakdown, issues);
  if (!quality) return null;
  return {
    label: t("operator.session_duration.quality", { quality }),
    detail: t("operator.session_duration.quality_detail", { quality }),
  };
}

/** Why a duration shows "—", in the active catalog: the explanation for a value the backend left unknown. */
export function durationUnknownReason(
  breakdown: DurationBreakdown | null | undefined,
  issues: readonly string[] | null | undefined,
  t: Translate = localization.t,
): string | null {
  const code = durationUnknownCode(breakdown, issues);
  if (!code) return null;
  return code === "charging_only" || code === "unknown"
    ? t("operator.session_duration.unknown_reason", { breakdown: code })
    : t("operator.session_duration.quality_detail", { quality: code });
}
